import type { Faq as FaqType } from 'libs/http/api/content/content.types';

import type { AccessRoute } from 'ui/organisms/AccessRoutes/AccessRoutes.types';
import type { NumberedRow } from 'ui/organisms/NumberedRowsSection/NumberedRowsSection.types';

type Translate = (key: string) => string;

export const getWhereItLandsRows = (t: Translate): NumberedRow[] => [
  {
    index: '01',
    title: t('Disputes and litigation'),
    body: t(
      'Confirm the correct defendant before proceedings are issued. Establish the registered office for service. Assess whether a judgment is worth pursuing by looking at filed accounts, charges and group assets before the costs run.',
    ),
  },
  {
    index: '02',
    title: t('Corporate and M&A'),
    body: t(
      'Build the subsidiary schedule from the registers rather than from a management spreadsheet. Verify signing authority against the officers on file. Pull the group as it stood at a given date for disclosure and warranty work.',
    ),
  },
  {
    index: '03',
    title: t('Restructuring and insolvency'),
    body: t(
      'Find the security already registered over the assets. Identify connected entities and common directorships across a group. Track insolvency events across jurisdictions where a debtor holds subsidiaries.',
    ),
  },
  {
    index: '04',
    title: t('Risk, compliance and client intake'),
    body: t(
      'Evidence client due diligence with a dated, sourced record instead of an undated screenshot. Identify beneficial owners through the chain. Screen the whole ownership structure, then keep it under watch for the life of the retainer.',
    ),
  },
  {
    index: '05',
    title: t('Real estate and finance'),
    body: t(
      "Check the counterparty is the registered proprietor's actual legal entity, that it is still active, and that the person signing holds office. Surface existing security before a facility is drawn.",
    ),
  },
  {
    index: '06',
    title: t('Business development and pricing'),
    body: t(
      'Registry events are legal-work signals. A charge registered means a financing. A new subsidiary means corporate work. An administration filing means restructuring. Firms use the change feed to reach the client before the panel review does.',
    ),
  },
];

export const getAccessRoutes = (t: Translate): AccessRoute[] => [
  {
    name: t('Platform'),
    desc: t('Search, screen and export company records from the web application.'),
    actions: [{ label: t('Open the platform'), href: '/platform' }],
  },
  {
    name: 'API',
    desc: t(
      'Query any company by registry identifier. Documented endpoints, JSON responses, provenance on every field.',
    ),
    actions: [{ label: t('Read the API docs'), href: 'https://api.globaldatabase.com/docs/v2/#setup' }],
  },
  {
    name: t('Bulk data feeds'),
    desc: t('Full datasets and scheduled deltas, delivered to your warehouse on your cadence.'),
    actions: [{ label: t('Request a feed'), href: '/book-a-demo' }],
  },
  {
    name: 'CRM',
    desc: (
      <>
        {t('Enrich and verify accounts inside')} <b>Salesforce</b>, <b>HubSpot</b> {t('and')}{' '}
        <b>Microsoft Dynamics 365</b>, {t('against the same registry records.')}
      </>
    ),
    actions: [
      {
        label: 'Salesforce',
        href: 'https://appexchange.salesforce.com/appxListingDetail?listingId=7d8cb601-add2-47f7-a345-4008cbb0cfe4',
      },
      {
        label: 'HubSpot',
        href: 'https://ecosystem.hubspot.com/marketplace/listing/global-database-intelligence-enrichment',
      },
      { label: 'Dynamics 365', href: '/microsoft-dynamics-integration' },
    ],
  },
  {
    name: 'MCP',
    desc: t('Connect the dataset to any MCP-compatible agent or tool.'),
    actions: [{ label: t('MCP server'), href: '/mcp-integration' }],
  },
  {
    name: t('Assistant connectors'),
    desc: t('Pull registry data straight into the assistant your teams already use.'),
    actions: [
      { label: t('Add to Claude'), href: 'https://claude.ai/new#directory/connectors/global-database' },
      {
        label: t('Add to ChatGPT'),
        href: 'https://chatgpt.com/plugins/plugin_asdk_app_696f807d21a481918a1ed1f43d719ce9',
      },
    ],
  },
  {
    name: 'Regis AI',
    desc: t('Ask questions in natural language across the dataset and get answers with their sources attached.'),
    actions: [{ label: t('Meet Regis AI'), href: '/regis' }],
  },
];

export const getFaqItems = (t: Translate): FaqType[] => [
  {
    id: 'where-does-the-data-come-from',
    default_opened: true,
    question: t('Where does the data actually come from?'),
    answer: t(
      'Government company registries, official gazettes and regulator filings across 200+ countries, taken directly from those sources. Each field in the record carries the registry it came from and the date the underlying document was filed. Where a value is derived rather than filed, such as an ownership percentage computed across several steps, the derivation is shown and the field is labelled.',
    ),
  },
  {
    id: 'can-we-rely-on-this-in-a-pleading',
    question: t('Can we rely on this in a pleading or a report?'),
    answer: t(
      'We provide the registered facts with their source and filing date so you can verify them against the primary record. That is the standard we hold ourselves to. Whether a given fact is sufficient for a particular filing is a judgement for the fee earner, and the licence includes redistribution rights so the record can go into a report, a schedule or an exhibit without a separate permission.',
    ),
  },
  {
    id: 'is-what-we-search-confidential',
    question: t('Is what we search confidential?'),
    answer: t(
      "Your searches are not shared with other customers, sold on, or used to inform anyone else's results. This matters more in legal than in most sectors, because a search can reveal a prospective matter or an adverse party before an engagement letter exists. If your risk team needs the position in writing, we will put it in the contract rather than leave it in a policy page.",
    ),
  },
  {
    id: 'how-does-this-differ-from-dnb-moodys-bvd',
    question: t("How does this differ from what we get from Dun & Bradstreet, Moody's or Bureau van Dijk?"),
    answer: t(
      'Three practical differences. We source directly from registries rather than through intermediaries, so the chain back to the filing is short and visible. We attach provenance at field level rather than at record level. And we use open identifiers with redistribution rights included, so nothing you build on top of the data is locked to us. Coverage and price are worth comparing directly. We will run a side-by-side on your own list of entities.',
    ),
  },
  {
    id: 'does-it-cover-offshore-and-harder-jurisdictions',
    question: t('Does it cover offshore and harder jurisdictions?'),
    answer: t(
      'Coverage spans 400+ registries including offshore centres, free zones and jurisdictions with limited electronic filing. Depth varies by registry because it varies at source. Some publish full officer and shareholder detail, others publish very little. We show you exactly what is available per jurisdiction before you buy rather than after.',
    ),
  },
  {
    id: 'can-we-put-the-output-into-work-we-bill-for',
    question: t('Can we put the output into work we bill for?'),
    answer: t(
      'Yes. Redistribution rights are included in the licence, so a record can appear in a report, a disclosure schedule, an exhibit or advice you charge for, without asking us each time. Identifiers are open, so a matter file stays portable if you ever move platform.',
    ),
  },
  {
    id: 'how-do-you-handle-personal-data',
    question: t('How do you handle personal data?'),
    answer: t(
      'Records include personal data about directors, officers and beneficial owners, taken from public registers where the registry itself publishes it. Every field carries its source and date, so your own record of processing can point at where it came from. The data protection terms, including the controller and processor positions and any transfer mechanisms, sit in the data processing agreement, which your risk team should review before onboarding.',
    ),
  },
  {
    id: 'can-we-get-certified-copies-of-documents',
    question: t('Can we get certified or official copies of documents?'),
    answer: t(
      'Underlying filed documents are available for many jurisdictions through the platform. Where a matter needs a certified or apostilled copy, tell us the jurisdiction and we will confirm what we can fulfil directly and where an agent is required.',
    ),
  },
  {
    id: 'how-does-monitoring-work-in-practice',
    question: t('How does monitoring work in practice?'),
    answer: t(
      'You put entities on a watchlist by matter, by client group, or in bulk. When a filing changes the record, the change is delivered by email, webhook or into your CRM, with the old value, the new value and the source. Firms typically watch client groups for the life of the retainer and opposing parties for the life of the matter.',
    ),
  },
  {
    id: 'how-long-does-it-take-to-get-running',
    question: t('How long does it take to get running?'),
    answer: t(
      'Platform access is same day. An API or bulk integration depends on what it connects to, and firms usually start with one practice group before wiring the record into intake or practice management. We will scope it against your systems rather than quote a number that assumes an easy case.',
    ),
  },
  {
    id: 'what-does-it-cost',
    question: t('What does it cost?'),
    answer: t(
      'Pricing is per seat with volume tiers on API and bulk. Firms usually start with one practice group and expand once the record is inside the intake workflow. See <a href="/pricing-products">pricing</a> or ask for a quote against your actual expected volume.',
    ),
  },
];
